import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  InputAdornment,
  Container,
} from "@mui/material";
import { DatePicker } from "antd";
import {
  Search as SearchIcon,
  Star as StarIcon,
  PlayArrow as PlayIcon,
  CheckCircle,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import ManageEventsOnGoImg from "../../../../Svg/newManageEvents.svg";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

export default function DiscoverPage() {
  const words = [
    "Simplified",
    "Perfected",
    "Customizable",
    "Seamless",
    "Smart",
  ];
  const [index, setIndex] = useState(0);
  const navigate = useNavigate();
  const [eventName, setEventName] = useState("");
  const [dateRange, setDateRange] = useState([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % words.length);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const handleDateChange = (dates) => {
    if (dates) {
      const [startDate, endDate] = dates;
      setDateRange([startDate.toDate(), endDate.toDate()]);
    } else {
      setDateRange([null, null]);
    }
  };

  const formatToUTC = (date) => {
    if (!date) return null;
    const d = new Date(date);
    d.setUTCHours(24, 0, 0, 0);
    return d.toISOString();
  };

  // Just return the date object directly
  const updateStartDate = (startDate) => {
    setDateRange((prev) => [startDate, prev?.[1] || null]);
  };

  const updateEndDate = (endDate) => {
    setDateRange((prev) => [prev?.[0] || null, endDate]);
  };

  const handleSearchChange = (e) => {
    setEventName(e.target.value);
  };

  const handleButtonClick = () => {
    const startDateParam = dateRange?.[0]
      ? `&startDate=${dateRange[0].toISOString()}`
      : "";
    const endDateParam = dateRange?.[1]
      ? `&endDate=${dateRange[1].toISOString()}`
      : "";
    navigate(
      `/search?eventName=${encodeURIComponent(
        eventName
      )}${startDateParam}${endDateParam}`
    );
  };

  const isMobile = window.innerWidth <= 600;

  return (
    <Box
      sx={{
        mt: { sm: 4, md: 10 },
        mb: { xs: 2, md: 15 },
        position: "relative",
      }}
    >
      <Container maxWidth="lg" sx={{ zIndex: 1 }}>
        <Grid
          container
          spacing={4}
          alignItems="center"
          justifyContent="center"
          sx={{ alignItems: "start" }}
        >
          <Grid item xs={12} md={5} lg={4.5} sx={{ order: { xs: 1, md: 1 } }}>
            <Box sx={{ marginTop: { xs: 3, md: 0 } }}>
              <Typography
                variant="h1"
                sx={{ fontSize: { xs: 24, sm: 24, md: 28 } }}
                fontWeight={700}
                gutterBottom
              >
                Your Go-To Digital Ticketing Platform,&nbsp;
                {isMobile ? (
                  <span>
                    <AnimatePresence mode="wait">
                      <motion.span
                        key={words[index]}
                        initial={{ x: 50, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: -50, opacity: 0 }}
                        transition={{ duration: 0.8, ease: "easeInOut" }}
                        style={{
                          position: "relative",
                          display: "inline-block",
                          backgroundImage:
                            "linear-gradient(90deg, #6a11cb, #2575fc, #ff6a00, #ff0080, #6a11cb)",
                          backgroundSize: "300% 300%",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          animation: "gradientAnimation 8s ease infinite",
                        }}
                      >
                        {words[index]}
                      </motion.span>
                    </AnimatePresence>
                  </span>
                ) : (
                  <span>
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={words[index]}
                      initial={{ y: -20, opacity: 0 }}
                      animate={{ y: -1, opacity: 1 }}
                      exit={{ y: -20, opacity: 0 }}
                      transition={{ duration: 0.6, ease: "easeInOut" }}
                      style={{
                        position: "absolute",
                        display: "inline-block",
                        backgroundImage:
                          "linear-gradient(90deg, #6a11cb, #2575fc, #ff6a00, #ff0080, #6a11cb)",
                        backgroundSize: "300% 300%",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        animation: "gradientAnimation 8s ease infinite",
                      }}
                    >
                      {words[index]}
                    </motion.span>
                  </AnimatePresence>
                </span>
                )}
               
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                gutterBottom
                sx={{
                  mt: {xs: 2, md: 0 },
                  textAlign: { xs: "left", md: "justify" },
                  hyphens: "auto",
                }}
              >
                Sell Tickets Online for Your Attraction, Festival, Concert,
                Museum, and More – No Contracts, Instant Payouts, and Effortless
                Ticketing with GX Tickets.
              </Typography>
              <Box mt={3} display="flex" gap={2} flexWrap="wrap">
                <Button
                  href="/eventmanagement"
                  variant="contained"
                  sx={{
                    borderRadius: "4px",
                    backgroundColor: "#3453B3",
                    fontWeight: 700,
                    padding: "10px 24px",
                    width: { xs: "100%", sm: "auto" },
                    position: "relative",
                    overflow: "hidden",
                    transition: "background-color 0.3s, color 0.3s",
                    "&:hover": {
                      backgroundColor: "rgb(246,246,246)",
                      color: "#3453B3",
                    },
                    "&::before, &::after": {
                      content: '""',
                      position: "absolute",
                      width: "0%",
                      height: "0%",
                      borderStyle: "solid",
                      borderColor: "#3453B3",
                      transition: "width 0.3s ease, height 0.3s ease",
                    },
                    "&::before": {
                      top: 0,
                      left: 0,
                      borderWidth: "2px 0 0 2px",
                    },
                    "&::after": {
                      bottom: 0,
                      right: 0,
                      borderWidth: "0 2px 2px 0",
                    },
                    "&:hover::before, &:hover::after": {
                      width: "100%",
                      height: "100%",
                    },
                  }}
                >
                  Design Tickets
                </Button>
                <Button
                  variant="text"
                  sx={{
                    fontWeight: 700,
                    padding: "10px 24px",
                    color: "#535353",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  <PlayIcon sx={{ color: "#5DAE1D" }} /> PLAY DEMO
                </Button>
              </Box>
              <Box alignItems="center" mt={2}>
                {[...Array(5)].map((_, index) => (
                  <StarIcon key={index} sx={{ color: "#FFA500" }} />
                ))}
                <Typography variant="body2">
                  Rated 5/5 -{" "}
                  <span style={{ color: "rgba(0, 0, 0, 0.6)" }}>
                    from over 200+ Reviews
                  </span>
                </Typography>
              </Box>
              <Box mt={2} display="flex" gap={2} flexWrap="wrap">
                <Typography
                  variant="body2"
                  sx={{
                    color: "#535353",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <CheckCircle /> Top-Rated Platform
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#535353",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <CheckCircle /> 100% Guarantee
                </Typography>
                {/* <Typography
                  variant="body2"
                  sx={{
                    color: "#535353",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <CheckCircle /> Award-Winning Support
                </Typography> */}
              </Box>

              <Box sx={{ mt: 2 }}>
                {/* <Typography sx={heroStyles.heading}>Download App</Typography> */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                    justifyContent: {xs: "center", sm: 'flex-start', md: "flex-start"},
                  }}
                >
                  {/* App Store Button */}
                  <Button
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      backgroundColor: "#000",
                      borderRadius: "12px",
                      px: { xs: 1.5, sm: 2 },
                      py: {xs: 4, sm: 1},
                      width: {xs: '70%', sm: 'auto'},
                      minWidth: { xs: "140px", sm: "160px" },
                      height: { xs: "44px", sm: "48px" },
                      "&:hover": {
                        backgroundColor: "#111",
                      },
                    }}
                    onClick={() =>
                      window.open("https://apps.apple.com/pk/app/gx-tickets/id6745892471", "_blank")
                    }
                  >
                    <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                      <img
                        src="/images/app-store.svg"
                        alt="App Store"
                        height="32"
                        style={{ borderRadius: "8px" }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        height: "100%",
                        lineHeight: 1,
                        
                      }}
                    >
                      <Typography
                        sx={{
                          color: "white",
                          fontSize: { xs: "9px", sm: "10px" },
                          letterSpacing: "0.5px",
                          fontWeight: 400,
                          textAlign: "left",
                        }}
                      >
                        Download on the
                      </Typography>
                      <Typography
                        sx={{
                          color: "white",
                          fontSize: { xs: "13px", sm: "14px" },
                          fontWeight: 600,
                          mt: "2px",
                          textAlign: "left",
                        }}
                      >
                        App Store
                      </Typography>
                    </Box>
                  </Button>

                  {/* Play Store Button */}
                  <Button
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      backgroundColor: "#000",
                      borderRadius: "12px",
                      px: { xs: 1.5, sm: 2 },
                      py: {xs: 4, sm: 1},
                      width: {xs: '70%', sm: 'auto'},
                      minWidth: { xs: "157px", sm: "160px" },
                      height: { xs: "44px", sm: "48px" },
                      "&:hover": {
                        backgroundColor: "#111",
                      },
                    }}
                    onClick={() =>
                      window.open(
                        "https://play.google.com/store/apps/details?id=com.npisoftware.gxtickets&pli=1",
                        "_blank"
                      )
                    }
                  >
                    <Box sx={{ display: "flex", alignItems: "center", height: "100%", ml: -1 }}>
                      <img
                        src="/images/play-store.svg"
                        alt="Play Store"
                        height="32"
                        style={{ borderRadius: "8px" }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        height: "100%",
                        lineHeight: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "white",
                          fontSize: { xs: "9px", sm: "10px" },
                          letterSpacing: "0.5px",
                          fontWeight: 400,
                          textAlign: "left",
                        }}
                      >
                        Get it on the
                      </Typography>
                      <Typography
                        sx={{
                          color: "white",
                          fontSize: { xs: "13px", sm: "14px" },
                          fontWeight: 600,
                          mt: "2px",
                          textAlign: "left",
                        }}
                      >
                        Play Store
                      </Typography>
                    </Box>
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={2.5}
            lg={3}
            sx={{
              // marginTop: { sm: 0, md: 13 },
              display: "flex",
              justifyContent: "center",
              order: { xs: 3, md: 2 },
            }}
          >
            <motion.img
              src={ManageEventsOnGoImg}
              alt="Create & Manage Events on the Go!"
              style={{ maxWidth: "100%", cursor: "pointer" }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.3 }}
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ order: { xs: 2, md: 3 } }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                pb: { xs: 0, md: 20 },
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#fff",
                  padding: { xs: "1.5rem", md: "2rem" },
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                  position: "relative",
                  zIndex: 1,
                  mb: { xs: 0, md: -25 },
                }}
              >
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  Search for Events
                </Typography>

                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search"
                  margin="normal"
                  value={eventName}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                {/* <DatePicker.RangePicker
                  className="custom-date-picker"
                  onChange={handleDateChange}
                  format="MM/DD/YYYY"
                  size="large"
                  style={{ width: "100%", marginBottom: "15px" }}
                  inputReadOnly
                /> */}
                <Box
                  sx={{
                    marginBottom: "8px",
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    height: "42px",
                    "& > *": {
                      height: "100%",
                    },
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="Start Date"
                      format={"MM/DD/YYYY"}
                      slotProps={{
                        textField: {
                          size: "large",
                          inputProps: { readOnly: true },
                        },
                      }}
                      onChange={(newValue) => updateStartDate(newValue)}
                      // onChange={(dates) => setDateRange(dates)}
                      renderInput={(params) => <TextField {...params} />}
                      inputReadOnly
                    />
                  </LocalizationProvider>
                </Box>

                <Box
                  sx={{
                    marginBottom: "8px",
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    height: "42px",
                    "& > *": {
                      height: "100%",
                    },
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="End Date"
                      format={"MM/DD/YYYY"}
                      slotProps={{
                        textField: {
                          size: "large",
                          inputProps: { readOnly: true },
                        },
                      }}
                      onChange={(newValue) => updateEndDate(newValue)}
                      // onChange={(dates) => setDateRange(dates)}
                      renderInput={(params) => <TextField {...params} />}
                      inputReadOnly
                    />
                  </LocalizationProvider>
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    borderRadius: "4px",
                    width: "100%",
                    backgroundColor: "#000000",
                    fontWeight: 700,
                    padding: "10px 24px",
                    position: "relative",
                    overflow: "hidden",
                    transition: "background-color 0.5s, color 0.3s",
                    "&:hover": {
                      backgroundColor: "rgb(246,246,246)",
                      color: "#000000",
                    },
                    "&::before, &::after": {
                      content: '""',
                      position: "absolute",
                      width: "0%",
                      height: "0%",
                      borderStyle: "solid",
                      borderColor: "#000000",
                      transition: "width 0.3s ease, height 0.3s ease",
                    },
                    "&::before": {
                      top: 0,
                      left: 0,
                      borderWidth: "2px 0 0 2px",
                    },
                    "&::after": {
                      bottom: 0,
                      right: 0,
                      borderWidth: "0 2px 2px 0",
                    },
                    "&:hover::before, &:hover::after": {
                      width: "100%",
                      height: "100%",
                    },
                  }}
                  onClick={handleButtonClick}
                >
                  LET'S GO
                </Button>
              </Box>
            </Box>
            <Box sx={{ marginTop: 10 }}>
              <Typography
                variant="h6"
                fontWeight={700}
                gutterBottom
                component={motion.div}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
                sx={{
                  textAlign: { xs: "center", md: "justify" },
                  hyphens: "auto",
                }}
              >
                Create Digital Tickets <br />& Manage Events on the Go!
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  mb: 2,
                  textAlign: { xs: "center", md: "justify" },
                  hyphens: "auto",
                }}
                component={motion.div}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                viewport={{ once: true }}
              >
                Effortlessly create digital tickets, manage events, and keep
                everything on track all from the convenience of your smartphone.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

const heroStyles = {
  heading: {
    fontSize: "16px",
    fontWeight: 700,
    marginBottom: "10px",
  },

  appIcons: {
    display: "flex",

    gap: "8px",
    flexWrap: "wrap",
  },
  iconStyle: {
    border: "1px solid grey",
    borderRadius: "4px",
  },
};
